/** Controls the visibility of QAL browser diagnostics for an environment. */
type QalEnvironmentConfig = {
  /** Whether QAL diagnostic messages may be written to the browser console. */
  showLogger: boolean;
  /** Whether the QAL startup banner may be written to the browser console. */
  showQalBanner: boolean;
};

/**
 * Defines the environments in which QAL browser diagnostics are visible.
 *
 * Logger and banner output is restricted to local development environments.
 * All higher environments suppress diagnostic output to prevent analytics
 * payloads and implementation details from being exposed in the console.
 */
export const QAL_ENVIRONMENT_CONFIG = {
  local: { showLogger: true, showQalBanner: true },
  dev: { showLogger: true, showQalBanner: true },
  development: { showLogger: true, showQalBanner: true },
  sit: { showLogger: false, showQalBanner: false },
  uat: { showLogger: false, showQalBanner: false },
  perf: { showLogger: false, showQalBanner: false },
  prod: { showLogger: false, showQalBanner: false },
  production: { showLogger: false, showQalBanner: false },
} as const satisfies Record<string, QalEnvironmentConfig>;

/** Environment names explicitly supported by the QAL diagnostic policy. */
export type QalEnvironment = keyof typeof QAL_ENVIRONMENT_CONFIG;

/**
 * Resolves the diagnostic configuration for an environment.
 *
 * Environment names are matched case-insensitively after surrounding
 * whitespace is removed. Unknown or missing environments are not resolved,
 * allowing public visibility checks to apply a secure hidden default.
 *
 * @param environment - Runtime environment name supplied by the host application.
 * @returns The matching diagnostic configuration, or `undefined` when unsupported.
 */
const getEnvironmentConfig = (
  environment?: string
): QalEnvironmentConfig | undefined => {
  const normalizedEnvironment = (environment || '')
    .trim()
    .toLowerCase() as QalEnvironment;

  return QAL_ENVIRONMENT_CONFIG[normalizedEnvironment];
};

/**
 * Determines whether the QAL logger may be shown in an environment.
 *
 * @param environment - Runtime environment name supplied by the host application.
 * @returns `true` when logger output is enabled; otherwise `false`. Unknown or
 * missing environments default to `false`.
 */
export const shouldShowQalLogger = (environment?: string): boolean =>
  getEnvironmentConfig(environment)?.showLogger ?? false;

/**
 * Determines whether the QAL startup banner may be shown in an environment.
 *
 * @param environment - Runtime environment name supplied by the host application.
 * @returns `true` when banner output is enabled; otherwise `false`. Unknown or
 * missing environments default to `false`.
 */
export const shouldShowQalBanner = (environment?: string): boolean =>
  getEnvironmentConfig(environment)?.showQalBanner ?? false;
